import { createMemo, createSignal, Show } from 'solid-js';
import { format, differenceInDays } from 'date-fns';
import { Link } from '@solidjs/router';
import { Box } from '../../layout/box/box';
import { Row } from '../../layout/row/row';
import { Upma as UpmaType } from '../product-types';
import { StyledText } from '../products.styles';
import { colors } from '../../../style/style-variables';
import {
    StyledUpmaContent,
    StyledUPMAInstructionsForUse as StyledUpmaInstructionsForUse,
    StyledUPMAInformation as StyledUpmaInformation,
    StyledUpmaContentContainer,
    StyledUpmaProductTitle,
    StyledUpmaInformationContainer,
    StyledMoreInformationTrigger,
} from './upma.styles';
import { Toggle } from '../../layout/toggle/toggle';
import { StyledLink } from '../../strategies/strategies.styles';
import { ArrowOut } from '../../svg/svg';

type Props = {
    upma: UpmaType;
};

const parseDate = (date: string | null) => {
    if (!date) {
        return null;
    }

    return format(new Date(date), 'yyy-MM-dd');
};

const showExiprationWarning = (date: string | null) => {
    if (!date) {
        return false;
    }

    const diff = differenceInDays(new Date(date), Date.now());

    return diff > 0 && diff < 60;
};

export const Upma = (props: Props) => {
    const [isOpen, setIsOpen] = createSignal(false);

    /**
     * Where to send someone when there is no document to link.
     *
     * KI has begun folding minor uses into a product's own decision and publishing
     * them only as data, with no UPMA annex — Pictor Active is one. For those the
     * conditions are readable in the "Användning" section of KI's product page, so
     * that page is the source, not a PDF. No `#product-decisions` anchor: that
     * jumps to the decision list, which is not where the minor uses are.
     */
    const decisionLink = createMemo(
        () =>
            props.upma.kiProductUrl ??
            `https://apps.kemi.se/BkmRegistret/Kemi.Spider.Web.External/Beslut/Details?beslutId=${props.upma.decisionId}&objekttypId=7`
    );
    const documentLink = createMemo(
        () =>
            props.upma.kiDocumentUrl ??
            `https://apps.kemi.se/BkmRegistret/Kemi.Spider.Web.External/File/GetDokument/${props.upma.documentId}`
    );

    return (
        <Box>
            <StyledUpmaContentContainer>
                <Show when={props.upma.registrationNumber}>
                    <StyledUpmaProductTitle>
                        <Show when={props.upma.registrationNumber?.product?.name}>
                            <strong>{props.upma.registrationNumber?.product?.name}</strong> -{' '}
                        </Show>
                        Regnr: {props.upma.registrationNumber?.registrationNumber}
                    </StyledUpmaProductTitle>
                </Show>
                <StyledUpmaContent>
                    <StyledUpmaInstructionsForUse>
                        <strong>Användning</strong>
                        <StyledText innerText={props.upma.areaOfUse || ''} />
                    </StyledUpmaInstructionsForUse>

                    <StyledUpmaInformation>
                        <Show when={showExiprationWarning(props.upma.approvedUntilDate)}>
                            <p>
                                <strong style={{ color: colors.red }}>
                                    OBS! Godkännandet upphör {parseDate(props.upma.approvedUntilDate)}
                                </strong>
                            </p>
                        </Show>

                        <Row justifyContent="space-between">
                            <strong>Godkänt</strong>
                            <span>{parseDate(props.upma.approvedDate)}</span>
                        </Row>
                        <Show when={props.upma.approvalCeasedDate}>
                            <Row justifyContent="space-between">
                                <strong>Godkännandet upphörde</strong>
                                <span>{parseDate(props.upma.approvalCeasedDate)}</span>
                            </Row>
                        </Show>
                        {/*
                          * KI's `fårAnvändasTom` is the last day the product may be
                          * used, not the first day it may not. The old label said
                          * "Användningsförbud fr.o.m", which read as one day earlier
                          * than the truth. This wording matches KI's own.
                          */}
                        <Show when={props.upma.usageForbiddenDate}>
                            <Row justifyContent="space-between">
                                <strong>Får användas till och med</strong>
                                <span>{parseDate(props.upma.usageForbiddenDate)}</span>
                            </Row>
                        </Show>

                        <Show when={props.upma.documentId || props.upma.decisionText}>
                            <Show
                                when={props.upma.documentId}
                                fallback={
                                    <>
                                        <StyledMoreInformationTrigger>
                                            <span onClick={() => setIsOpen(!isOpen())}>Ytterligare villkor</span>
                                            <Toggle isOpen={isOpen()} onClick={() => setIsOpen(!isOpen())} />
                                        </StyledMoreInformationTrigger>
                                        <StyledUpmaInformationContainer classList={{ 'is-visible': isOpen() }}>
                                            <StyledText innerText={props.upma.decisionText || ''} />
                                        </StyledUpmaInformationContainer>
                                    </>
                                }
                            >
                                <Row justifyContent="space-between">
                                    <strong>Ytterligare villkor</strong>
                                    <StyledLink target="_blank" href={documentLink()}>
                                        Visa PDF
                                        <ArrowOut />
                                    </StyledLink>
                                </Row>
                            </Show>
                        </Show>

                        {/*
                          * KI publishes no annex for a minor use it has folded into
                          * the product's own decision. The conditions are then only
                          * on KI's product page, so that is what we point at.
                          */}
                        <Show when={!props.upma.documentId && !props.upma.decisionText}>
                            <Row justifyContent="space-between">
                                <strong>Källa</strong>
                                <StyledLink target="_blank" href={decisionLink()}>
                                    Kemikalieinspektionen
                                    <ArrowOut />
                                </StyledLink>
                            </Row>
                        </Show>
                    </StyledUpmaInformation>
                </StyledUpmaContent>
            </StyledUpmaContentContainer>
        </Box>
    );
};
